import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { invokeExtensionV2, type ExtensionV2Response } from "@/lib/api";
import { monthLabel } from "./cultivation-dates";
import {
  evidenceCoverage,
  profileLabel,
  REQUIRED_PROFILES,
} from "./cultivation-readiness";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { capabilityAllowed } from "./cultivation-capabilities";
import type { CultivationImport, CultivationWorkspace } from "./cultivation-types";

const MAX_FILE_BYTES = 700 * 1024;
const CHUNK_SIZE = 3_000;
const PROFILES = [
  { value: "auto", label: "Detect profile" },
  { value: "plant_inventory", label: "Plant inventory" },
  { value: "harvest", label: "Harvest" },
  { value: "plants_destroyed", label: "Plants destroyed" },
  { value: "transfers", label: "Transfers" },
  { value: "wholesale_transfers", label: "Wholesale transfers" },
  { value: "package_inventory", label: "Package inventory" },
] as const;

type WorkbookChunk = {
  source_document_id: string;
  file_name: string;
  checksum_sha256: string;
  byte_length: number;
  offset: number;
  next_offset: number;
  chunk_base64: string;
  eof: boolean;
};

type ImportPreview = {
  profile: string;
  row_count: number;
  checksum_sha256: string;
  period_start: string;
  period_end: string;
};

type FileStatus = "pending" | "checking" | "ready" | "conflict" | "duplicate" | "error" | "importing" | "imported";

type PendingFile = {
  id: string;
  file: File;
  requestedProfile: string;
  profile: string | null;
  rowCount: number | null;
  checksum: string | null;
  chunks: string[] | null;
  validatedLicenseId: string | null;
  validatedPeriodStart: string | null;
  validatedPeriodEnd: string | null;
  status: FileStatus;
  replace: boolean;
  error: string | null;
};

type ImportScope = {
  licenseId: string;
  periodStart: string;
  periodEnd: string;
};

function scopeKey(scope: ImportScope): string {
  return [scope.licenseId, scope.periodStart, scope.periodEnd].join("|");
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

async function filePayload(file: File): Promise<{ checksum: string; chunks: string[] }> {
  if (!/\.xlsx?$/i.test(file.name)) throw new Error("Choose a METRC .xls or .xlsx report.");
  if (file.size === 0 || file.size > MAX_FILE_BYTES) {
    throw new Error(`The report must be smaller than ${MAX_FILE_BYTES / 1024} KiB.`);
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource));
  const checksum = [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = btoa(binary);
  const chunks: string[] = [];
  for (let offset = 0; offset < base64.length; offset += CHUNK_SIZE) {
    chunks.push(base64.slice(offset, offset + CHUNK_SIZE));
  }
  return { checksum, chunks };
}

function coverageKey(profile: string, licenseId: string, start: string, end: string): string {
  return [profile, licenseId, start, end].join("|");
}

function base64Bytes(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function sha256Hex(bytes: Uint8Array): Promise<string> {
  return crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource).then((digest) =>
    [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join(""),
  );
}

function downloadText(name: string, text: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function CultivationImports({
  workspace,
  onOpenReview,
}: {
  workspace: CultivationWorkspace;
  onOpenReview: () => void;
}) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noActivity, setNoActivity] = useState<{ profile: string; month: string } | null>(null);
  const [noActivityReason, setNoActivityReason] = useState("");
  const [savingNoActivity, setSavingNoActivity] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const canImport = capabilityAllowed(workspace.capabilities, "import");
  const currentScope: ImportScope = {
    licenseId: workspace.licenseId,
    periodStart: workspace.periodStart,
    periodEnd: workspace.periodEnd,
  };
  const currentScopeKey = scopeKey(currentScope);
  const scopeKeyRef = useRef(currentScopeKey);
  scopeKeyRef.current = currentScopeKey;
  const lastScopeKey = useRef(currentScopeKey);

  useEffect(() => {
    if (lastScopeKey.current === currentScopeKey) return;
    lastScopeKey.current = currentScopeKey;
    setFiles((current) => current.map((item) =>
      item.status === "imported"
        ? item
        : {
          ...item,
          profile: null,
          rowCount: null,
          validatedLicenseId: null,
          validatedPeriodStart: null,
          validatedPeriodEnd: null,
          status: "pending",
          replace: false,
          error: null,
        }
    ));
    setNoActivity(null);
    setNoActivityReason("");
    setError(null);
    setNotice("The location or period changed. Validate the pack again before importing.");
  }, [currentScopeKey]);

  const coverage = evidenceCoverage(
    workspace.imports.imports,
    workspace.licenseId,
    workspace.periodStart,
    workspace.periodEnd,
    workspace.imports.attestations,
  );
  const currentByCoverage = useMemo(() => {
    const map = new Map<string, { checksum_sha256: string; source_document_id: string }>();
    for (const item of workspace.imports.imports) {
      map.set(
        coverageKey(item.profile, item.license_id, item.period_start, item.period_end),
        { checksum_sha256: item.checksum_sha256, source_document_id: item.source_document_id },
      );
    }
    return map;
  }, [workspace.imports.imports]);

  function updateFile(id: string, patch: Partial<PendingFile>) {
    setFiles((current) => current.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  async function previewFile(item: PendingFile, validationScope: ImportScope): Promise<PendingFile> {
    if (!workspace.installation) throw new Error("The cultivation costing extension is not installed.");
    if (!validationScope.licenseId) throw new Error("Choose a location before validating reports.");
    if (!validationScope.periodStart || !validationScope.periodEnd) throw new Error("Choose the report period.");
    const payload = item.checksum && item.chunks
      ? { checksum: item.checksum, chunks: item.chunks }
      : await filePayload(item.file);
    const response: ExtensionV2Response<ImportPreview> = await invokeExtensionV2<ImportPreview>(
      workspace.organizationId,
      workspace.installation.id,
      {
        kind: "ui_command",
        app: "niche-accounting",
        payload: {
          command: "niche.metrc.import.preview",
          values: {
            profile: item.requestedProfile,
            license_id: validationScope.licenseId,
            period_start: validationScope.periodStart,
            period_end: validationScope.periodEnd,
            file_name: item.file.name,
            checksum_sha256: payload.checksum,
          },
          viewId: "costing",
          selectedIds: [],
          renderToken: crypto.randomUUID(),
          hostContext: { import: { file_base64_chunks: payload.chunks } },
        },
      },
    );
    if (!response.ok || !response.result) {
      throw new Error(response.error?.message ?? "The report could not be validated.");
    }
    const detected = response.result;
    const existing = currentByCoverage.get(coverageKey(
      detected.profile,
      validationScope.licenseId,
      validationScope.periodStart,
      validationScope.periodEnd,
    ));
    const status: FileStatus = existing?.checksum_sha256 === payload.checksum
      ? "duplicate"
      : existing
        ? "conflict"
        : "ready";
    return {
      ...item,
      profile: detected.profile,
      rowCount: detected.row_count,
      checksum: payload.checksum,
      chunks: payload.chunks,
      validatedLicenseId: validationScope.licenseId,
      validatedPeriodStart: validationScope.periodStart,
      validatedPeriodEnd: validationScope.periodEnd,
      status,
      replace: status === "conflict" ? item.replace : false,
      error: status === "duplicate"
        ? "That exact report is already current for this profile and period."
        : status === "conflict"
          ? "A different report already covers this profile and period. Choose Replace to supersede it."
          : null,
    };
  }

  async function validate(items: PendingFile[]) {
    const validationScope = currentScope;
    const validationKey = scopeKey(validationScope);
    setBusy(true);
    setError(null);
    setNotice(null);
    const next: PendingFile[] = [];
    for (const item of items) {
      updateFile(item.id, { status: "checking", error: null });
      try {
        const checked = await previewFile(item, validationScope);
        next.push(scopeKeyRef.current === validationKey
          ? checked
          : {
            ...item,
            status: "pending",
            profile: null,
            validatedLicenseId: null,
            validatedPeriodStart: null,
            validatedPeriodEnd: null,
            error: "The location or period changed. Validate the pack again.",
          });
      } catch (cause) {
        next.push({ ...item, status: "error", error: errorMessage(cause) });
      }
    }
    setFiles((current) => {
      const byId = new Map([...current, ...items].map((item) => [item.id, item]));
      return [...byId.values()].map((item) => next.find((candidate) => candidate.id === item.id) ?? item);
    });
    setBusy(false);
  }

  async function addFiles(selected: FileList | null) {
    if (!selected || selected.length === 0) return;
    const additions: PendingFile[] = [...selected].map((file) => ({
      id: crypto.randomUUID(),
      file,
      requestedProfile: "auto",
      profile: null,
      rowCount: null,
      checksum: null,
      chunks: null,
      validatedLicenseId: null,
      validatedPeriodStart: null,
      validatedPeriodEnd: null,
      status: "pending",
      replace: false,
      error: null,
    }));
    setFiles((current) => [...current, ...additions]);
    await validate(additions);
  }

  async function importReady() {
    if (!workspace.installation) {
      setError("The cultivation costing extension is not installed.");
      return;
    }
    const ready = files.filter((item) =>
      item.profile &&
      (item.status === "ready" || (item.status === "conflict" && item.replace)));
    if (ready.length === 0) {
      setError("Validate at least one importable report before importing.");
      return;
    }
    const mismatched = ready.filter((item) =>
      item.validatedLicenseId !== workspace.licenseId ||
      item.validatedPeriodStart !== workspace.periodStart ||
      item.validatedPeriodEnd !== workspace.periodEnd);
    if (mismatched.length > 0) {
      for (const item of mismatched) {
        updateFile(item.id, {
          status: "pending",
          profile: null,
          validatedLicenseId: null,
          validatedPeriodStart: null,
          validatedPeriodEnd: null,
          error: "The location or period changed. Validate the pack again.",
        });
      }
      setError("The location or period changed after validation. Validate the pack again before importing.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    let imported = 0;
    for (const item of ready) {
      updateFile(item.id, { status: "importing", error: null });
      try {
        if (
          !item.profile ||
          !item.checksum ||
          !item.chunks ||
          !item.validatedLicenseId ||
          !item.validatedPeriodStart ||
          !item.validatedPeriodEnd
        ) {
          throw new Error("Validate the report before importing.");
        }
        const response = await invokeExtensionV2<{ saved: boolean; row_count: number; profile: string }>(
          workspace.organizationId,
          workspace.installation.id,
          {
            kind: "ui_command",
            app: "niche-accounting",
            payload: {
              command: "niche.metrc.import",
              values: {
                profile: item.profile,
                license_id: item.validatedLicenseId,
                period_start: item.validatedPeriodStart,
                period_end: item.validatedPeriodEnd,
                file_name: item.file.name,
                checksum_sha256: item.checksum,
                correction: item.replace ? "replace" : "new",
              },
              viewId: "costing",
              selectedIds: [],
              renderToken: crypto.randomUUID(),
              hostContext: { import: { file_base64_chunks: item.chunks } },
            },
          },
        );
        if (!response.ok || !response.result) {
          throw new Error(response.error?.message ?? "The report could not be imported.");
        }
        imported += response.result.saved ? 1 : 0;
        updateFile(item.id, {
          status: "imported",
          error: response.result.saved ? null : "That exact report was already imported.",
        });
      } catch (cause) {
        updateFile(item.id, { status: "error", error: errorMessage(cause) });
      }
    }
    setBusy(false);
    setNotice(imported === 0
      ? "No new report revisions were saved."
      : `Imported ${imported} report revision${imported === 1 ? "" : "s"}.`);
    await workspace.reloadImports();
  }

  async function downloadOriginal(item: CultivationImport) {
    if (!workspace.installation || !workspace.organizationId) {
      setError("The cultivation costing extension is not installed.");
      return;
    }
    if (!item.retention?.download_available) {
      setError("The original workbook is not retained for this import.");
      return;
    }
    setDownloading(item.source_document_id);
    setError(null);
    setNotice(null);
    try {
      const chunks: Uint8Array[] = [];
      let offset = 0;
      let fileName = item.retention.file_name || item.file_name;
      let expectedChecksum = item.retention.checksum_sha256 || item.checksum_sha256;
      for (let index = 0; index < 1000; index += 1) {
        const response = await invokeExtensionV2<WorkbookChunk>(
          workspace.organizationId,
          workspace.installation.id,
          {
            kind: "ui_command",
            app: "niche-accounting",
            payload: {
              command: "niche.metrc.workbook.download",
              values: {
                source_document_id: item.source_document_id,
                offset: String(offset),
              },
              viewId: "costing",
              selectedIds: [],
              renderToken: crypto.randomUUID(),
            },
          },
        );
        if (!response.ok || !response.result) {
          throw new Error(response.error?.message ?? "The retained workbook is unavailable.");
        }
        const chunk = response.result;
        chunks.push(base64Bytes(chunk.chunk_base64));
        fileName = chunk.file_name || fileName;
        expectedChecksum = chunk.checksum_sha256 || expectedChecksum;
        if (chunk.eof) break;
        if (chunk.next_offset <= offset) throw new Error("The retained workbook download did not advance.");
        offset = chunk.next_offset;
      }
      const bytes = new Uint8Array(chunks.reduce((total, chunk) => total + chunk.byteLength, 0));
      let cursor = 0;
      for (const chunk of chunks) {
        bytes.set(chunk, cursor);
        cursor += chunk.byteLength;
      }
      if (expectedChecksum && await sha256Hex(bytes) !== expectedChecksum) {
        throw new Error("The retained workbook failed its checksum check.");
      }
      const type = /\.xls$/i.test(fileName) ? "application/vnd.ms-excel" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([bytes], { type }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
      setNotice(`Downloaded ${fileName}.`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setDownloading(null);
    }
  }

  async function saveNoActivity() {
    if (!noActivity || !workspace.installation) return;
    if (!noActivityReason.trim()) {
      setError("A no-activity reason is required.");
      return;
    }
    setSavingNoActivity(true);
    setError(null);
    setNotice(null);
    try {
      const response = await invokeExtensionV2(
        workspace.organizationId,
        workspace.installation.id,
        {
          kind: "ui_command",
          app: "niche-accounting",
          payload: {
            command: "niche.metrc.no_activity.set",
            values: {
              profile: noActivity.profile,
              license_id: workspace.licenseId,
              month: noActivity.month,
              reason: noActivityReason.trim(),
            },
            viewId: "costing",
            selectedIds: [],
            renderToken: crypto.randomUUID(),
          },
        },
      );
      if (!response.ok) throw new Error(response.error?.message ?? "The no-activity attestation was rejected.");
      setNoActivity(null);
      setNoActivityReason("");
      setNotice(`Marked ${profileLabel(noActivity.profile)} for ${noActivity.month} as no activity.`);
      await workspace.reloadImports();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSavingNoActivity(false);
    }
  }

  function downloadErrors() {
    const rows = files
      .filter((item) => item.error)
      .map((item) => [item.file.name, item.error ?? ""].map((value) => `"${value.replaceAll('"', '""')}"`).join(","));
    if (rows.length === 0) return;
    downloadText("metrc-import-errors.csv", ["file,error", ...rows].join("\n"));
  }

  const importable = files.filter((item) =>
    item.profile && (item.status === "ready" || (item.status === "conflict" && item.replace))).length;
  const readyCoverage = new Set(
    workspace.imports.imports
      .filter((item) => item.license_id === workspace.licenseId && item.period_start === workspace.periodStart && item.period_end === workspace.periodEnd)
      .map((item) => item.profile),
  );

  return (
    <div className="grid gap-5">
      <section className="surface grid gap-4 rounded-2xl border border-[var(--border)] p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Import pack</h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              Add one or more METRC portal exports. The extension reads each workbook; the host stores no original file.
            </p>
          </div>
          <Badge className="ms-auto" tone={coverage.ready ? "success" : "warning"}>
            {coverage.ready ? "Evidence ready" : `${coverage.missing.length} missing`}
          </Badge>
        </div>

        {!canImport ? <CultivationCapabilityNotice capability="import" action="upload, replace, or attest METRC evidence" /> : null}
        <label className="grid gap-1 text-sm">
          Reports
          <input
            type="file"
            accept=".xls,.xlsx"
            multiple
            disabled={busy || !workspace.licenseId || !canImport}
            onChange={(event) => {
              const selected = event.target.files;
              event.target.value = "";
              void addFiles(selected);
            }}
            className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2"
          />
          <span className="text-xs text-[var(--muted-foreground)]">
            One report sheet per file, at most 700 KiB each. Profile is detected from the report headers.
          </span>
        </label>

        {files.length > 0 ? (
          <div className="grid gap-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">File</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Profile</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Rows</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Status</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((item) => (
                    <tr key={item.id}>
                      <td className="px-2 py-1">{item.file.name}</td>
                      <td className="px-2 py-1">
                        <select
                          aria-label={`Profile for ${item.file.name}`}
                          className="min-h-9 rounded-md border border-[var(--border)] bg-[var(--surface)] px-2"
                          value={item.requestedProfile}
                          disabled={busy || !canImport || item.status === "importing" || item.status === "imported"}
                          onChange={(event) => {
                            const requestedProfile = event.target.value;
                            updateFile(item.id, { requestedProfile, status: "pending", profile: null, error: null });
                            void validate([{ ...item, requestedProfile }]);
                          }}
                        >
                          {PROFILES.map((profile) => (
                            <option key={profile.value} value={profile.value}>{profile.label}</option>
                          ))}
                        </select>
                        {item.profile ? <span className="ms-2 text-xs">{profileLabel(item.profile)}</span> : null}
                      </td>
                      <td className="px-2 py-1">{item.rowCount ?? "—"}</td>
                      <td className="px-2 py-1">
                        <Badge tone={
                          item.status === "imported"
                            ? "success"
                            : item.status === "error"
                              ? "danger"
                              : item.status === "conflict" || item.status === "duplicate"
                                ? "warning"
                                : "neutral"
                        }>{item.status}</Badge>
                        {item.error ? <p className="mt-1 max-w-sm text-xs text-[var(--danger)]">{item.error}</p> : null}
                      </td>
                      <td className="px-2 py-1">
                        {item.status === "conflict" ? (
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={item.replace}
                              disabled={busy || !canImport}
                              onChange={(event) => updateFile(item.id, { replace: event.target.checked, status: event.target.checked ? "conflict" : "conflict" })}
                            />
                            Replace
                          </label>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busy || !canImport || item.status === "importing"}
                            onClick={() => setFiles((current) => current.filter((candidate) => candidate.id !== item.id))}
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => void validate(files)} disabled={busy || !canImport || files.length === 0}>
                Validate pack
              </Button>
              <Button type="button" onClick={() => void importReady()} disabled={busy || !canImport || importable === 0}>
                {busy ? "Working…" : `Import ${importable || ""} ready report${importable === 1 ? "" : "s"}`}
              </Button>
              <Button type="button" variant="ghost" onClick={downloadErrors} disabled={!files.some((item) => item.error)}>
                Download errors
              </Button>
            </div>
          </div>
        ) : null}

        {error ? <p role="alert" className="text-sm text-[var(--danger)]">{error}</p> : null}
        {notice ? <p role="status" className="text-sm text-[var(--muted-foreground)]">{notice}</p> : null}
      </section>

      <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Coverage</h2>
          <span className="text-xs text-[var(--muted-foreground)]">
            {workspace.periodStart}–{workspace.periodEnd}
          </span>
          <Button className="ms-auto" type="button" variant="secondary" size="sm" onClick={onOpenReview} disabled={!coverage.ready}>
            Review close inputs
          </Button>
        </div>
        {coverage.months.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">Choose a valid period to see coverage.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Report</th>
                  {coverage.months.map((month) => (
                    <th key={month} className="border-b border-[var(--border)] px-2 py-1 text-left">{monthLabel(month)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {REQUIRED_PROFILES.map((profile) => (
                  <tr key={profile.id}>
                    <td className="px-2 py-1">{profile.label}</td>
                    {coverage.months.map((month) => {
                      const cell = coverage.cells.find((candidate) => candidate.profile === profile.id && candidate.month === month);
                      return (
                        <td key={month} className="px-2 py-1">
                          <div className="grid gap-1">
                            <Badge tone={cell?.status === "ready" || cell?.status === "no_activity" ? "success" : "warning"}>
                              {cell?.status === "ready" ? "Ready" : cell?.status === "no_activity" ? "No activity" : "Missing"}
                            </Badge>
                            {cell?.status === "missing" ? (
                              <button
                                type="button"
                                className="text-left text-xs text-[var(--action)] underline-offset-4 hover:underline disabled:opacity-50"
                                disabled={!canImport}
                                onClick={() => {
                                  setNoActivity({ profile: profile.id, month });
                                  setNoActivityReason("");
                                  setError(null);
                                }}
                              >
                                Mark no activity
                              </button>
                            ) : null}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {Array.from(readyCoverage).filter((profile) => !REQUIRED_PROFILES.some((required) => required.id === profile)).length > 0 ? (
          <p className="text-xs text-[var(--muted-foreground)]">
            Also current: {Array.from(readyCoverage).filter((profile) => !REQUIRED_PROFILES.some((required) => required.id === profile)).map(profileLabel).join(", ")}.
          </p>
        ) : null}
      </section>

      <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5">
        <h2 className="text-sm font-semibold">Import history</h2>
        {workspace.imports.imports.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">No imports yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Profile</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Period</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">File</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Revision</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Rows</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Checksum</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Original</th>
                </tr>
              </thead>
              <tbody>
                {workspace.imports.imports.map((item) => (
                  <tr key={item.source_document_id}>
                    <td className="px-2 py-1">{profileLabel(item.profile)}</td>
                    <td className="px-2 py-1">{item.period_start}–{item.period_end}</td>
                    <td className="px-2 py-1">{item.file_name}</td>
                    <td className="px-2 py-1">{item.revision}</td>
                    <td className="px-2 py-1">{item.row_count}</td>
                    <td className="px-2 py-1 font-mono text-xs">{item.checksum_sha256.slice(0, 12)}…</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={item.retention?.state === "retained" ? "success" : item.retention?.state === "not_configured" ? "neutral" : "warning"}>
                          {item.retention?.state === "retained" ? "Retained" : item.retention?.state === "not_configured" ? "Not retained" : item.retention?.state ?? "Unknown"}
                        </Badge>
                        {item.retention?.download_available ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={downloading !== null}
                            onClick={() => void downloadOriginal(item)}
                          >
                            {downloading === item.source_document_id ? "Downloading…" : "Download source"}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-[var(--muted-foreground)]">
          Current evidence only. Replaced revisions remain in the extension database but are not listed here.
        </p>
      </section>

      <Dialog
        open={Boolean(noActivity)}
        onOpenChange={(open) => {
          if (!open) setNoActivity(null);
        }}
        title="Mark month as no activity"
        description={`${noActivity ? profileLabel(noActivity.profile) : "This report"} has no activity for ${noActivity?.month ?? "the selected month"}. The attestation is stored with the selected license and reviewed at close.`}
      >
        <div className="grid gap-3">
          <label className="grid gap-1 text-sm">
            Reason
            <textarea
              className="min-h-24 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
              value={noActivityReason}
              onChange={(event) => setNoActivityReason(event.target.value)}
              placeholder="Explain why there is no activity for this report and month"
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setNoActivity(null)} disabled={savingNoActivity}>Cancel</Button>
            <Button type="button" onClick={() => void saveNoActivity()} disabled={savingNoActivity}>
              {savingNoActivity ? "Saving…" : "Save attestation"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
